import React, { useState, useEffect } from 'react';
import { MessageSquare, ArrowLeft, User } from 'lucide-react';

const API_BASE = '/.netlify/functions';

const CommentsLog = ({
  setView,
  setSelectedWorkflowId,
  navigate,
  isEmbedded = false
}) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    fetchComments();
  }, []);

  const fetchComments = async (cursor = null) => {
    if (cursor) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      let url = `${API_BASE}/marketplace-comments?global=true&limit=20`;
      if (cursor) {
        url += `&cursor=${encodeURIComponent(cursor)}`;
      }

      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch comments');

      const data = await response.json();

      if (cursor) {
        setComments(prev => [...prev, ...data.comments]);
      } else {
        setComments(data.comments);
      }
      setNextCursor(data.nextCursor);
      setTotalCount(data.total || 0);
    } catch (err) {
      console.error('Error fetching global comments:', err);
      setError('Failed to load comments');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleWorkflowClick = (listingId) => {
    navigate(`/exchange/${listingId}${isEmbedded ? '?embedded=true' : ''}`);
  };

  return (
    <div className={`min-h-screen bg-black text-white ${isEmbedded ? 'pt-4 px-4 py-4' : 'pt-20 px-4 py-12'}`}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => setView('browse')}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Exchange
          </button>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <MessageSquare className="h-8 w-8 text-blue-400" />
            Comments Log
          </h1>
          <p className="text-gray-400 mt-2">Recent comments across all workflows</p>
        </div>

        {/* Comments list */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-400">{error}</div>
        ) : comments.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <MessageSquare className="h-12 w-12 mx-auto mb-3" />
            <p className="text-lg">No comments yet</p>
            <p className="text-sm mt-1">Be the first to comment on a workflow!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {comments.map((comment) => (
              <div key={comment.id} className="bg-gray-900 border border-gray-800 rounded-lg p-4 hover:border-gray-700 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-sm mb-1">
                      <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="text-white font-medium">{comment.display_name}</span>
                      <span className="text-gray-600">·</span>
                      <span className="text-gray-500">{formatDate(comment.created_at)}</span>
                      {comment.edited_at && (
                        <span className="text-gray-600 text-xs">(edited)</span>
                      )}
                    </div>
                    <p className="text-gray-300 text-sm whitespace-pre-wrap line-clamp-3">{comment.body}</p>
                  </div>
                </div>
                {comment.listing && (
                  <div className="mt-3 pt-3 border-t border-gray-800">
                    <button
                      onClick={() => handleWorkflowClick(comment.listing.id)}
                      className="flex items-center gap-3 text-sm text-gray-400 hover:text-blue-400 transition-colors group"
                    >
                      {comment.listing.thumbnail_url && (
                        <img
                          src={comment.listing.thumbnail_url}
                          alt=""
                          className="w-8 h-8 rounded object-cover flex-shrink-0"
                        />
                      )}
                      <span className="group-hover:underline truncate">
                        {comment.listing.title}
                      </span>
                    </button>
                  </div>
                )}
              </div>
            ))}

            {/* Load More */}
            {nextCursor && (
              <button
                onClick={() => fetchComments(nextCursor)}
                disabled={loadingMore}
                className="w-full py-3 text-blue-400 hover:text-blue-300 text-sm transition-colors"
              >
                {loadingMore ? 'Loading...' : `Load more comments (${comments.length} of ${totalCount})`}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentsLog;
